import { daekyoSpaceAPI } from '@/api';
import DaekyoHeader from './DaekyoHeader';
import {
  Content,
  LoadingMessage,
  SectionContainer,
  SectionContent,
  SectionHeader,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper,
} from '@/components/CommonStyles';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactApexChart from 'react-apexcharts';
import { palette } from '@/modules/defines/styles';
import '@/modules/defines/font.css';

const DaekyoDataContainer = () => {
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('monthly');
  const [expandedLegends, setExpandedLegends] = useState<{
    [key: number]: boolean;
  }>({});
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [hiddenSeries, setHiddenSeries] = useState<{ [key: number]: string[] }>(
    {}
  );

  // LOAD Data
  const { data: dataAPI, isLoading: isLoadingAPI } = useQuery({
    queryKey: ['getStatisticsDaekyo'],
    queryFn: () =>
      daekyoSpaceAPI.getStatistics({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const toggleLegend = (chartIndex: number) => {
    setExpandedLegends((prev) => ({
      ...prev,
      [chartIndex]: !prev[chartIndex],
    }));
  };

  const handleLegendHover = (seriesName: string, chartIndex: number) => {
    setHoveredSeries(seriesName);

    const chartContainers = document.querySelectorAll(
      `.chart-container-${chartIndex}`
    );
    chartContainers.forEach((container) => {
      container.classList.add('series-highlighted');

      const seriesElements = container.querySelectorAll('.apexcharts-series');
      seriesElements.forEach((series, index) => {
        const currentChart = chartData?.[chartIndex];
        if (currentChart && currentChart.series[index]?.name === seriesName) {
          series.classList.add('highlighted');
        } else {
          series.classList.remove('highlighted');
        }
      });
    });
  };

  const handleLegendLeave = (chartIndex: number) => {
    setHoveredSeries(null);

    const chartContainers = document.querySelectorAll(
      `.chart-container-${chartIndex}`
    );
    chartContainers.forEach((container) => {
      container.classList.remove('series-highlighted');

      const seriesElements = container.querySelectorAll('.apexcharts-series');
      seriesElements.forEach((series) => {
        series.classList.remove('highlighted');
      });
    });
  };

  // Bar Chart 옵션
  const barChartOptions = {
    chart: {
      type: 'bar' as const,
      height: 350,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      zoom: {
        enabled: true,
        type: 'x' as const,
        autoScaleYaxis: true,
      },
      selection: {
        enabled: true,
        type: 'x' as const,
      },
      pan: {
        enabled: true,
        type: 'x' as const,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded',
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val;
      },
      style: {
        fontSize: '11px',
        fontWeight: '600',
      },
    },
    stroke: {
      show: false,
      width: 1,
      colors: ['transparent'],
    },
    xaxis: {
      title: {
        text: '날짜',
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: '이용자 수',
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      y: {
        formatter: function (val: number) {
          return val + ' 명';
        },
      },
    },
  };

  // Line Chart 옵션
  const lineChartOptions = {
    chart: {
      type: 'line' as const,
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    stroke: {
      curve: 'smooth' as const,
      width: 3,
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      title: {
        text: '날짜',
      },
      labels: {
        rotate: -45,
        style: {
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      title: {
        text: '접속자 수',
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      style: {
        zIndex: 9999,
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        const value = series[seriesIndex][dataPointIndex];

        if (value === 0) {
          return '';
        }

        let tooltipContent = `<div class="apexcharts-tooltip-box" style="background: rgba(0, 0, 0, 0.8); color: white; padding: 8px; border-radius: 4px; font-size: 10px; line-height: 1.2;">`;

        const validSeries = [];
        for (let i = 0; i < series.length; i++) {
          const seriesValue = series[i][dataPointIndex];
          if (seriesValue > 0) {
            validSeries.push({
              name: w.globals.seriesNames[i],
              value: seriesValue,
            });
          }
        }

        validSeries.sort((a, b) => b.value - a.value);

        const maxItems = 16;
        const itemsToShow = validSeries.slice(0, maxItems);
        const hasMoreItems = validSeries.length > maxItems;

        itemsToShow.forEach((item) => {
          tooltipContent += `<span>${item.name}: <strong style="font-weight: 800">${item.value} 회</strong></span><br/>`;
        });

        if (hasMoreItems) {
          tooltipContent += `<span style="color: #888; font-style: italic;">... 외 ${
            validSeries.length - maxItems
          }개 항목</span><br/>`;
        }

        tooltipContent += `</div>`;
        return tooltipContent;
      },
    },
  };

  // 월별 데이터 집계 함수
  const aggregateMonthlyData = (dates: string[], data: number[]) => {
    const monthlyMap: { [key: string]: number } = {};

    dates.forEach((date, index) => {
      const month = date.substring(0, 7); // "2025-10" 형식
      if (!monthlyMap[month]) {
        monthlyMap[month] = 0;
      }
      monthlyMap[month] += data[index];
    });

    const sortedMonths = Object.keys(monthlyMap).sort();
    const monthlyData = sortedMonths.map((month) => monthlyMap[month]);

    return {
      categories: sortedMonths,
      data: monthlyData,
    };
  };

  // CHART DATA
  const chartData = useMemo(() => {
    if (!dataAPI?.data) return null;

    const { dates, data1, data2 } = dataAPI.data;

    // 첫 번째 차트 데이터 (일별/월별 전환)
    let chart1Categories = dates;
    let chart1Data = data1;

    if (viewType === 'monthly') {
      const monthlyResult = aggregateMonthlyData(dates, data1);
      chart1Categories = monthlyResult.categories;
      chart1Data = monthlyResult.data;
    }

    return [
      {
        id: 1,
        title: '서비스 이용자 수',
        description: ' ',
        categories: chart1Categories,
        series: [
          {
            name: '이용자 수',
            data: chart1Data,
          },
        ],
        options: barChartOptions,
        type: 'bar',
      },
      {
        id: 2,
        title: '화면별 접속자수',
        description: ' ',
        categories: dates,
        series: data2.map((item: any) => ({
          name: item.label,
          data: item.value,
        })),
        options: lineChartOptions,
        type: 'line',
      },
    ];
  }, [dataAPI, viewType]);

  // 필터링된 시리즈 데이터 생성 (색상 보존)
  const getFilteredSeries = (chartIndex: number) => {
    if (!chartData) return [];

    const chart = chartData[chartIndex];
    if (!chart) return [];

    const defaultColors = [
      '#008FFB',
      '#00E396',
      '#FEB019',
      '#FF4560',
      '#775DD0',
      '#546E7A',
      '#26a69a',
      '#D10CE8',
      '#FF9800',
      '#795548',
    ];

    const hiddenSeriesForChart = hiddenSeries[chartIndex] || [];
    return chart.series
      .filter((series: any) => !hiddenSeriesForChart.includes(series.name))
      .map((series: any) => {
        const originalIndex = chart.series.findIndex(
          (s: any) => s.name === series.name
        );
        return {
          ...series,
          color: defaultColors[originalIndex % defaultColors.length],
        };
      });
  };

  // 차트용 데이터 슬라이싱 함수
  const getChartData = (chart: any) => {
    if (!chart) {
      return {
        categories: chart?.categories || [],
        series: chart?.series || [],
        needsScroll: false,
      };
    }

    const totalLength = chart.categories.length;

    if (totalLength <= 14) {
      return {
        categories: chart.categories,
        series: chart.series,
        needsScroll: false,
      };
    }

    return {
      categories: chart.categories,
      series: chart.series,
      needsScroll: true,
    };
  };

  if (isLoadingAPI) {
    return (
      <>
        <DaekyoHeader />
        <Content>
          <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>
        </Content>
      </>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <>
        <DaekyoHeader />
        <Content>
          <LoadingMessage>데이터가 없습니다.</LoadingMessage>
        </Content>
      </>
    );
  }

  return (
    <Container>
      <DaekyoHeader />
      <Content>
        {chartData && (
          <SectionWrapper>
            {chartData.map((chart, index) => (
              <SectionContainer key={chart.id}>
                {index === 0 ? (
                  <CustomSectionHeader>
                    <TitleContainer>
                      <SectionTitle>{chart.title}</SectionTitle>
                      <SectionSubTitle>{chart.description}</SectionSubTitle>
                    </TitleContainer>
                    <TabContainer>
                      <Tab
                        active={viewType === 'monthly'}
                        onClick={() => setViewType('monthly')}
                      >
                        월별
                      </Tab>
                      <Tab
                        active={viewType === 'daily'}
                        onClick={() => setViewType('daily')}
                      >
                        일별
                      </Tab>
                    </TabContainer>
                  </CustomSectionHeader>
                ) : (
                  <SectionHeader>
                    <SectionTitle>{chart.title}</SectionTitle>
                    <SectionSubTitle>{chart.description}</SectionSubTitle>
                  </SectionHeader>
                )}
                <SectionContent>
                  {(() => {
                    const chartDataInfo = getChartData(chart);

                    if (chartDataInfo.needsScroll) {
                      return (
                        <ChartWithFixedTitles>
                          <FixedAxisTitles>
                            {chart.options.xaxis?.title?.text && (
                              <XAxisTitle>
                                {chart.options.xaxis.title.text}
                              </XAxisTitle>
                            )}
                            {chart.options.yaxis?.title?.text && (
                              <YAxisTitle>
                                {chart.options.yaxis.title.text}
                              </YAxisTitle>
                            )}
                          </FixedAxisTitles>
                          <ScrollableChartWrapper
                            className={`chart-container-${index}`}
                            ref={(ref) => {
                              if (ref) {
                                setTimeout(() => {
                                  ref.scrollLeft = ref.scrollWidth * 0.5;
                                }, 100);
                              }
                            }}
                          >
                            <ScrollableChart $isBarChart={chart.type === 'bar'}>
                              <ReactApexChart
                                options={
                                  {
                                    ...chart.options,
                                    chart: {
                                      ...chart.options.chart,
                                      width:
                                        chart.type === 'bar' ? '300%' : '200%',
                                      toolbar: {
                                        show: false,
                                      },
                                    } as any,
                                    xaxis: {
                                      ...chart.options.xaxis,
                                      categories: chartDataInfo.categories,
                                      title: {
                                        ...chart.options.xaxis?.title,
                                        text: undefined,
                                      },
                                    },
                                    yaxis: {
                                      ...chart.options.yaxis,
                                      title: {
                                        ...chart.options.yaxis?.title,
                                        text: undefined,
                                      },
                                      labels: {
                                        show: true,
                                        style: {
                                          fontSize: '11px',
                                          fontWeight: '400',
                                          colors: ['#6B7280'],
                                        },
                                      },
                                    },
                                    stroke: {
                                      curve: 'smooth',
                                      width: 2,
                                    },
                                    states: {
                                      hover: {
                                        filter: {
                                          type: 'none',
                                        },
                                      },
                                      active: {
                                        allowMultipleDataPointsSelection: false,
                                        filter: {
                                          type: 'none',
                                        },
                                      },
                                    },
                                    legend: {
                                      show: false,
                                    },
                                    plotOptions:
                                      chart.type === 'bar'
                                        ? {
                                            ...(chart.options as any)
                                              ?.plotOptions,
                                            bar: {
                                              ...(chart.options as any)
                                                ?.plotOptions?.bar,
                                              columnWidth: '40%',
                                            },
                                          }
                                        : (chart.options as any)?.plotOptions ||
                                          {},
                                  } as any
                                }
                                series={getFilteredSeries(index)}
                                type={chart.type as any}
                                height={350}
                              />
                            </ScrollableChart>
                          </ScrollableChartWrapper>
                          {chart.type === 'line' && (
                            <FixedLegendContainer>
                              <LineChartLegendContainer>
                                {chart.series.map(
                                  (series: any, seriesIndex: number) => {
                                    const hiddenSeriesForChart =
                                      hiddenSeries[index] || [];
                                    const isHidden =
                                      hiddenSeriesForChart.includes(
                                        series.name
                                      );
                                    const isHovered =
                                      hoveredSeries === series.name;

                                    const defaultColors = [
                                      '#008FFB',
                                      '#00E396',
                                      '#FEB019',
                                      '#FF4560',
                                      '#775DD0',
                                      '#546E7A',
                                      '#26a69a',
                                      '#D10CE8',
                                      '#FF9800',
                                      '#795548',
                                    ];

                                    const originalIndex = chart.series.findIndex(
                                      (s: any) => s.name === series.name
                                    );
                                    const seriesColor =
                                      defaultColors[
                                        originalIndex % defaultColors.length
                                      ];

                                    return (
                                      <LineChartLegendItem
                                        key={seriesIndex}
                                        isHovered={isHovered}
                                        isHidden={isHidden}
                                        seriesColor={seriesColor}
                                        onMouseEnter={() =>
                                          handleLegendHover(series.name, index)
                                        }
                                        onMouseLeave={() =>
                                          handleLegendLeave(index)
                                        }
                                        onClick={() => {
                                          const currentHidden =
                                            hiddenSeries[index] || [];
                                          const isCurrentlyHidden =
                                            currentHidden.includes(series.name);

                                          if (isCurrentlyHidden) {
                                            setHiddenSeries((prev) => ({
                                              ...prev,
                                              [index]: currentHidden.filter(
                                                (name) => name !== series.name
                                              ),
                                            }));
                                          } else {
                                            setHiddenSeries((prev) => ({
                                              ...prev,
                                              [index]: [
                                                ...currentHidden,
                                                series.name,
                                              ],
                                            }));
                                          }
                                        }}
                                      >
                                        <LineChartLegendColor
                                          color={seriesColor}
                                        />
                                        <LineChartLegendName>
                                          {series.name}
                                        </LineChartLegendName>
                                      </LineChartLegendItem>
                                    );
                                  }
                                )}
                              </LineChartLegendContainer>
                            </FixedLegendContainer>
                          )}
                        </ChartWithFixedTitles>
                      );
                    }

                    return (
                      <ChartContainer className={`chart-container-${index}`}>
                        <ReactApexChart
                          options={
                            {
                              ...chart.options,
                              stroke: {
                                curve: 'smooth',
                                width: 2,
                              },
                              states: {
                                hover: {
                                  filter: {
                                    type: 'none',
                                  },
                                },
                                active: {
                                  allowMultipleDataPointsSelection: false,
                                  filter: {
                                    type: 'none',
                                  },
                                },
                              },
                              chart: {
                                ...chart.options.chart,
                                toolbar: {
                                  ...chart.options.chart?.toolbar,
                                  show: chart.type === 'bar',
                                },
                              } as any,
                              xaxis: {
                                ...chart.options.xaxis,
                                categories: chartDataInfo.categories,
                              },
                              yaxis: {
                                ...chart.options.yaxis,
                                labels: {
                                  show: true,
                                  style: {
                                    fontSize: '11px',
                                    fontWeight: '400',
                                    colors: ['#6B7280'],
                                  },
                                },
                              },
                            } as any
                          }
                          series={chartDataInfo.series}
                          type={chart.type as any}
                          height={350}
                        />
                      </ChartContainer>
                    );
                  })()}
                </SectionContent>
              </SectionContainer>
            ))}
          </SectionWrapper>
        )}
      </Content>
    </Container>
  );
};

export default DaekyoDataContainer;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChartContainer = styled.div`
  width: 100%;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .apexcharts-tooltip {
    z-index: 9999 !important;
  }

  .apexcharts-series path,
  .apexcharts-series circle {
    transition: opacity 0.3s ease;
  }

  &.series-highlighted .apexcharts-series path,
  &.series-highlighted .apexcharts-series circle {
    opacity: 0.3;
  }

  &.series-highlighted .apexcharts-series.highlighted path,
  &.series-highlighted .apexcharts-series.highlighted circle {
    opacity: 1;
    stroke-width: 3;
  }

  .apexcharts-toolbar {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 8px;
  }

  .apexcharts-toolbar-icon {
    fill: #6b7280;
    cursor: pointer;
    transition: fill 0.2s ease;

    &:hover {
      fill: #374151;
    }
  }

  .apexcharts-pan-icon,
  .apexcharts-download-icon,
  .apexcharts-zoom-icon,
  .apexcharts-zoomin-icon,
  .apexcharts-zoomout-icon,
  .apexcharts-reset-icon {
    fill: #6b7280;
    cursor: pointer;
    transition: fill 0.2s ease;

    &:hover {
      fill: #374151;
    }
  }

  .apexcharts-canvas {
    overflow: visible;
  }

  .apexcharts-zoomable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .apexcharts-selection-rect {
    fill: rgba(59, 130, 246, 0.1);
    stroke: #3b82f6;
    stroke-width: 1;
    stroke-dasharray: 4 4;
  }
`;

const ScrollableChartWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 8px;

  .apexcharts-tooltip {
    z-index: 9999 !important;
  }

  .apexcharts-series path,
  .apexcharts-series circle {
    transition: opacity 0.3s ease;
  }

  &.series-highlighted .apexcharts-series path,
  &.series-highlighted .apexcharts-series circle {
    opacity: 0.3;
  }

  &.series-highlighted .apexcharts-series.highlighted path,
  &.series-highlighted .apexcharts-series.highlighted circle {
    opacity: 1;
    stroke-width: 3;
  }

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #cbd5e1, #94a3b8);
    border-radius: 6px;
    border: 2px solid #f1f5f9;

    &:hover {
      background: linear-gradient(90deg, #94a3b8, #64748b);
    }
  }

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
`;

const ScrollableChart = styled.div<{ $isBarChart?: boolean }>`
  width: ${({ $isBarChart }) => ($isBarChart ? '300%' : '200%')};
  min-width: ${({ $isBarChart }) => ($isBarChart ? '1200px' : '800px')};
`;

const ChartWithFixedTitles = styled.div`
  position: relative;
  width: 100%;
  padding: 20px 60px 40px 60px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .apexcharts-tooltip {
    z-index: 9999 !important;
  }
`;

const FixedAxisTitles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const XAxisTitle = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui,
    Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR',
    'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const YAxisTitle = styled.div`
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%) rotate(-90deg);
  transform-origin: center;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui,
    Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR',
    'Malgun Gothic', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
`;

const FixedLegendContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 60px;
  right: 60px;
  z-index: 15;
  pointer-events: auto;
`;

const LineChartLegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
`;

const LineChartLegendItem = styled.div<{
  isHovered?: boolean;
  isHidden?: boolean;
  seriesColor?: string;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.5rem;
  border-radius: 6px;
  opacity: ${({ isHidden }) => (isHidden ? 0.4 : 1)};
  background: ${({ isHovered, seriesColor }) =>
    isHovered ? `${seriesColor}15` : 'transparent'};
  border: 1px solid
    ${({ isHovered, seriesColor }) => (isHovered ? seriesColor : 'transparent')};

  &:hover {
    background: ${({ seriesColor }) => `${seriesColor}15`};
    border-color: ${({ seriesColor }) => seriesColor};
    transform: translateY(-1px);
  }
`;

const LineChartLegendColor = styled.div<{ color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ color }) => color};
  flex-shrink: 0;
`;

const LineChartLegendName = styled.span`
  white-space: nowrap;
`;

const CustomSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 8px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 0.5rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ active }) => (active ? palette.systemBlue : 'transparent')};
  color: ${({ active }) => (active ? 'white' : '#64748b')};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    sans-serif;

  &:hover {
    background: ${({ active }) =>
      active ? palette.systemBlue : 'rgba(100, 116, 139, 0.1)'};
  }
`;
